(() => {
    'use strict';

    const SELECTOR = '[data-hero-banner]';
    const teardowns = new WeakMap();

    const connectionAllowsVideo = () => {
        const connection = navigator.connection ?? navigator.mozConnection ?? navigator.webkitConnection;
        if (!connection) return true;
        if (connection.saveData) return false;
        return !/(^|-)2g$/.test(connection.effectiveType ?? '');
    };

    const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const whenIdle = (callback) => {
        if ('requestIdleCallback' in window) {
            window.requestIdleCallback(callback, { timeout: 2000 });
            return;
        }
        setTimeout(callback, 200);
    };

    const afterLoad = (callback) => {
        if (document.readyState === 'complete') {
            whenIdle(callback);
            return;
        }
        window.addEventListener('load', () => whenIdle(callback), { once: true });
    };

    const setupBanner = (root) => {
        if (root.dataset.heroBannerReady) return;
        root.dataset.heroBannerReady = 'true';

        const video = root.querySelector('.hb__video');
        if (!video) return;

        const desktopSrc = root.dataset.videoDesktop ?? '';
        const mobileSrc = root.dataset.videoMobile ?? '';
        if (!desktopSrc && !mobileSrc) return;
        if (prefersReducedMotion() || !connectionAllowsVideo()) return;

        const controller = new AbortController();
        const { signal } = controller;
        const breakpoint = Number.parseInt(root.dataset.breakpoint, 10) || 767;
        const mobileQuery = window.matchMedia(`(max-width: ${breakpoint}px)`);
        const autoplay = root.dataset.autoplay !== 'false';

        let currentSrc = '';
        let isVisible = true;

        const play = () => {
            if (!autoplay || !isVisible) return;
            video.play()?.catch(() => {});
        };

        const applySource = () => {
            const src = mobileQuery.matches ? mobileSrc : desktopSrc;

            if (!src) {
                root.classList.remove('hb--video-ready');
                if (!video.paused) video.pause();
                return;
            }

            if (src === currentSrc) {
                root.classList.add('hb--video-ready');
                play();
                return;
            }

            currentSrc = src;
            video.src = src;
            video.load();
            play();
        };

        video.addEventListener('loadeddata', () => root.classList.add('hb--video-ready'), { signal });

        if (typeof mobileQuery.addEventListener === 'function') {
            mobileQuery.addEventListener('change', applySource, { signal });
        } else {
            mobileQuery.addListener(applySource);
        }

        if (!('IntersectionObserver' in window)) {
            applySource();
            teardowns.set(root, () => controller.abort());
            return;
        }

        const observer = new IntersectionObserver(
            (entries) => {
                for (const entry of entries) {
                    isVisible = entry.isIntersecting;

                    if (!entry.isIntersecting) {
                        if (!video.paused) video.pause();
                    } else if (currentSrc) {
                        play();
                    } else {
                        applySource();
                    }
                }
            },
            { rootMargin: '200px 0px' }
        );

        observer.observe(root);

        teardowns.set(root, () => {
            observer.disconnect();
            controller.abort();
            video.pause();
            video.removeAttribute('src');
            video.load();
        });
    };

    const eachBanner = (scope, callback) => {
        const container = scope?.querySelectorAll ? scope : document;
        for (const banner of container.querySelectorAll(SELECTOR)) callback(banner);
    };

    const init = (scope) => eachBanner(scope, setupBanner);

    const destroy = (scope) =>
        eachBanner(scope, (banner) => {
            teardowns.get(banner)?.();
            teardowns.delete(banner);
            delete banner.dataset.heroBannerReady;
        });

    window.HeroBanner = { init, destroy };

    afterLoad(() => init(document));

    document.addEventListener('shopify:section:load', ({ target }) => init(target));
    document.addEventListener('shopify:section:unload', ({ target }) => destroy(target));
})();
